import { computed, ref } from 'vue';
import type { Content, ContentType } from '@ilteducation/content-registry-types';
import { defineStore } from 'pinia';
import persistenceOptions from './content-store-persistence';
import contentApi from './api/content-api';

export type State<C> = {
  content: Map<string, C>;
  isFetching: boolean;
};

export type StateTree<C> = {
  state: State<C>;
};

const createContentStore = <C extends Content>(contentType: ContentType) => {
  const { apiGetById, apiGetContentDownloadUrl } = contentApi;
  return defineStore(
    `content-${contentType}`,
    () => {
      const state = ref<State<C>>({
        content: new Map(),
        isFetching: false,
      });

      const getContent = computed(() => state.value.content);
      const isFetching = computed(() => state.value.isFetching);

      const getById = async (id: string): Promise<C | undefined> => {
        if (state.value.content.has(id)) {
          return state.value.content.get(id) as C;
        }

        state.value.isFetching = true;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        await apiGetById<C>(id).then((c) => state.value.content.set(c.id, c));

        state.value.isFetching = false;

        return state.value.content.get(id) as C;
      };

      const getByIds = async (ids: string[]): Promise<C[]> => {
        state.value.isFetching = true;
        const idsNotFound = ids.filter((id) => ![...state.value.content.keys()].includes(id));
        if (idsNotFound.length !== 0) {
          // eslint-disable-next-line no-restricted-syntax
          for (const id of idsNotFound) {
            try {
              // eslint-disable-next-line no-await-in-loop
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              await apiGetById<C>(id).then((с) => state.value.content.set(с.id, с));
            } catch {
              // Couldn't find material, so nothing is added to state
            }
          }
        }
        state.value.isFetching = false;
        return [...state.value.content.values()].filter((content) => ids.includes(content.id)) as C[];
      };

      return {
        state,
        content: getContent,
        isFetching,
        getById,
        getByIds,
        getContentDownloadUrl: apiGetContentDownloadUrl,
      };
    },
    {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      persist: persistenceOptions,
    },
  );
};

export default createContentStore;
